// src/pages/AuraClinic.jsx
import React, { useState } from "react";
import MegaMenu from "../components/MegaMenu/MegaMenu";
import Footer from "../components/footer/Footer";
import "./auraclinic.css"; // <-- import custom CSS
import Button from "../components/CtaButton";
import TrustedPartner from "../components/trustedPartners/TrustedPartner";
import AuraBanner from "../components/AuraClinicHomePageComponents/AuraBanner/AuraBanner";
import AuraWhyUs from "../components/AuraClinicHomePageComponents/AuraWhyUs/AuraWhyUs";
import AuraTestimonial from "../components/AuraClinicHomePageComponents/AuraTestimonial/AuraTestimonial";
import AuraIntigration from "../components/AuraClinicHomePageComponents/AuraIntigrationSection/AuraIntigration";
import AuraCTASection from "../components/AuraClinicHomePageComponents/AuraCtaSection/AuraCTASection";
import FAQ from "../components/Fqas/FAQ";
import AuraBusinessChallenges from "../components/AuraClinicHomePageComponents/AuraBusinessChallenges/AuraBusinessChallenges";
import SoftwareCompanyCta from "../components/software-company-cta/SoftwareCompanyCta";
import AuraTrustedSlider from "../components/AuraClinicHomePageComponents/AuraTrustedSlider/AuraTrustedSlider";
import AuraSecondCta from "../components/AuraClinicHomePageComponents/AuraSecondCta/AuraSecondCta";
import SecurityFIrstSection from "../components/SecurityFirstSection/SecurityFIrstSection";
import AuraContactForm from "../components/AuraClinicHomePageComponents/AuraContactForm/AuraContactForm";
import AuraArchitectureSection from "../components/AuraClinicHomePageComponents/AuraArchitectureSection/AuraArchitectureSection";
import TrendsInsights from "../components/trends-n-insights/TrendsInsights";

export default function AuraClinic() {
    const [activeStep, setActiveStep] = useState(0);

    const privacyDisclaimer =
        "We collect this information to provide you with the highest quality of care. Our practice follows the guidelines of the Royal College of General Practitioners handbook for the management of health information in private medical practice. Your information is kept private and secure as required by Federal law. By signing or submitting this form, you acknowledge that you have read and understood our practice privacy policy.";

    const onboardingSteps = [
        {
            step: 1,
            title: "Personal Information",
            description: "Name, address, DOB, contact.",
            iconClass: "fa-solid fa-user",
        },
        {
            step: 2,
            title: "Medical Information",
            description: "Allergies, conditions, medications.",
            iconClass: "fa-solid fa-pills",
        },
        {
            step: 3,
            title: "Insurance Details",
            description: "Medicare or private insurance.",
            iconClass: "fa-solid fa-credit-card",
        },
        {
            step: 4,
            title: "Preferences",
            description: "Preferred doctor, language.",
            iconClass: "fa-solid fa-gear",
        },
        {
            step: 5,
            title: "Document Upload",
            description: "ID, referrals, prescriptions.",
            iconClass: "fa-solid fa-upload",
        },
        {
            step: 6,
            title: "Consent & Privacy",
            description: "Digital signature + privacy confirmation.",
            iconClass: "fa-solid fa-pen",
        },
    ];

    const aiFeatures = [
        {
            iconClass: "fa-solid fa-comment-dots",
            title: "AI-assisted triage",
            description: "Analyses symptoms and recommends appointment type.",
        },
        {
            iconClass: "fa-solid fa-file-lines",
            title: "Clinical documentation support",
            description: "Summaries of patient notes to assist doctors.",
        },
        {
            iconClass: "fa-solid fa-file-circle-check",
            title: "Automated form validation",
            description: "Flags missing or inconsistent information.",
        },
        {
            iconClass: "fa-solid fa-receipt",
            title: "MBS guidance",
            description: "Suggests relevant MBS items based on clinical details.",
        },
    ];

    const mainFeatures = [
        {
            iconClass: "fa-solid fa-user",
            title: "Digital Patient Onboarding",
            features: [
                "Smart multi-step onboarding",
                "ID, consent, Medicare details",
                "Auto verification",
            ],
        },
        {
            iconClass: "fa-solid fa-dollar-sign",
            title: "MBS Compliance & Billing Support",
            features: ["Eligibility checks", "Reduces claim errors", "MBS item suggestions"],
        },
        {
            iconClass: "fa-solid fa-calendar-days",
            title: "Intelligent Appointment Scheduling",
            features: [
                "Confirmations & reminders",
                "Doctor availability sync",
                "Reduced no-shows",
            ],
        },
    ];

    const placeholderLogos = ["MC", "HN", "WH", "SC", "PM", "AC", "MH", "FC", "BC"];

    const progressWidth =
        (activeStep / (onboardingSteps.length - 1 || 1)) * 85;


    // FAQ DATA
    const faqData = [
        {
            id: "collapseOne",
            question: "Does Aura Clinic integrate with Best Practice (BP)?",
            answer: "Yes. Aura Clinic securely syncs patient data from BP during onboarding, reducing manual entry and ensuring accurate information across appointments and MBS workflows."
        },
        {
            id: "collapseTwo",
            question: "How does QR-enabled onboarding work?",
            answer: "Patients scan a QR code at your clinic, which opens a guided intake form. Aura Clinic retrieves their BP information (when available), allowing them to verify or update details without paperwork."
        },
        {
            id: "collapseThree",
            question: "Can patients complete onboarding before arriving at the clinic?",
            answer: "Yes. Clinics can send onboarding links to patients ahead of their appointment for faster check-in."
        },
        {
            id: "collapseFour",
            question: "Does Aura Clinic support real-time practitioner availability?",
            answer: "Yes. Practitioners can set their availability, and the system automatically prevents double-booking or scheduling conflicts."
        },
        {
            id: "collapseFive",
            question: "Can patients book appointments themselves?",
            answer: "Clinics can enable patient self-booking or keep booking internal — the system supports both options."
        },
        {
            id: "collapseSix",
            question: "Are appointment reminders included?",
            answer: "Yes. Automated reminders can be sent to reduce no-shows and improve attendance rates."
        },
        {
            id: "collapseSeven",
            question: "How does patient check-in and check-out work?",
            answer: "Patients scan the clinic QR code when they arrive and when they leave. The system updates practitioner dashboards instantly."
        },
        {
            id: "collapseEight",
            question: "What if a patient forgets to check in or out?",
            answer: "Staff can manually check them in or out from the clinic dashboard to maintain accurate records."
        },
        {
            id: "collapseNine",
            question: "Can practitioners see patient information before the consultation?",
            answer: "Yes. Practitioners receive a structured pre-consult summary based on patient intake and history."
        },
        {
            id: "collapseTen",
            question: "How does Aura Clinic use AI?",
            answer: "Aura Clinic uses advanced healthcare AI models to generate pre-consult summaries, assist with post-consult documentation, and provide structured, context-aware information to support clinical decisions."
        },
        {
            id: "collapseEleven",
            question: "Does AI replace the practitioner’s judgment?",
            answer: "No. AI provides support, not decision-making. Practitioners always remain in full control of the clinical process."
        }
    ];

    // BUSINESS CHALENGES
    const featuresData = [
        {
            title: "Digital Onboarding",
            description: "Streamline intake with QR-enabled forms and secure BP data syncing, reducing paperwork and improving patient flow from the moment they arrive."
        },
        {
            title: "Appointments & Scheduling",
            description: "Optimise practitioner schedules with real-time availability, smart booking tools, and automated reminders that reduce no-shows and enhance clinic coordination."
        },
        {
            title: "Pre-Consultation Summary (AI-Supported)",
            description: "Equip clinicians with structured pre-consult summaries generated from patient onboarding data and medical history—helping them start every consultation fully informed."
        },
        {
            title: "Post-Consultation Patient Report",
            description: "Automatically create clear, accurate after-visit reports that summarise key findings, recommendations, and follow-up actions, improving continuity of care."
        },
        {
            title: "MBS Workflow & Compliance",
            description: "Ensure accurate claim preparation with structured MBS workflows, real-time validations, and automated checks that reduce errors and save administrative time."
        },
        {
            title: "Patient Flow & Attendance Management",
            description: "Monitor arrivals, waiting times, and check-in/out activity in real time. Gain visibility into patient movement across the clinic to reduce bottlenecks and improve efficiency."
        }
    ];

    // SOFTWARE COMPANY CTA
    const companyCta = {
        heading: "Ready to streamline your clinic?",
        subHeading: "Review Aura Clinic's impact on flow and MBS accuracy with a specialist.",
        buttonText: "Book a demo today",
    }

    return (
        <>
            <MegaMenu />

            {/* ================= HERO ================= */}
            <AuraBanner />
            <AuraTrustedSlider category={"auraclinic"} />
            <AuraArchitectureSection />
            <AuraTestimonial />
            <AuraWhyUs />
            <AuraIntigration />
            <AuraCTASection />
            <AuraSecondCta />
            <AuraBusinessChallenges mainHeading={"Capabilities That Power Modern Clinical Workflows"} description={"Aura Clinic brings together the essential touchpoints of the patient journey—onboarding, appointments, attendance, clinical insights and MBS-ready reporting. Each capability works independently yet integrates seamlessly, giving clinics a unified, intelligent view of operations and patient care."} content={featuresData} />
            <SecurityFIrstSection />
            <AuraContactForm />
            <FAQ customClass={"aura-faq-section-main"} ClassForArrowColor={"aura-faq-section"} faqs={faqData}
                mainHeading="Frequently Asked Questions"
            />
            <TrendsInsights customClass={"aura-blog-section"} category="aura-clinic" />
            <SoftwareCompanyCta customClass={'aura-cta-section'} content={companyCta} />

            {/* ================= TRUSTED BY ================= */}
            {/* <section className="trusted-section-box-main">
                <div className="container">
                    <div className="row g-4 align-items-center">
                        <div className="col-lg-6">
                            <h2 className="h2 fw-bold text-aura-primary trusted-section-main-text mb-4">
                                Trusted by <span className="text-aura-accent">2,500+</span> clinics
                                for safe, secure and compliant healthcare workflows
                            </h2>
                            <div className="d-flex flex-wrap gap-3 mt-3">
                                <Button text="See Customer Stories" link="#" />
                                <Button text="Trust Center" link="#" appearance="outline" />
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <div className="row g-3">
                                {placeholderLogos.map((logo, idx) => (
                                    <div className="col-4" key={idx}>
                                        <div className="aura-logo-card d-flex align-items-center justify-content-center">
                                            <div className="aura-logo-inner d-flex align-items-center justify-content-center">
                                                <span className="fw-bold text-aura-primary">{logo}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section> */}

            {/* ================= PRIVACY STRIP ================= */}
            {/* <section className="aura-privacy-strip">
                <div className="container">
                    <div className="aura-privacy-card d-flex align-items-start gap-3">
                        <div className="aura-icon-badge flex-shrink-0">
                            <i className="fa-solid fa-shield-halved" />
                        </div>
                        <div>
                            <h3 className="h4 fw-semibold text-aura-primary mb-2">
                                Privacy & Data Protection
                            </h3>
                            <p className="description-text">{privacyDisclaimer}</p>
                        </div>
                    </div>
                </div>
            </section> */}

            {/* ================= MAIN FEATURES ================= */}
            {/* <section className="aura-section-bg">
                <div className="container">
                    <div className="text-center mb-4">
                        <h2 className="main-feature-heading">
                            Everything you need for smooth, secure and efficient{" "}
                            <span className="text-aura-accent">healthcare</span>
                        </h2>
                        <p className="description-text">
                            Comprehensive tools designed for modern Australian medical practices.
                        </p>
                    </div>

                    <div className="row mt-3 g-4">
                        {mainFeatures.map((feature, idx) => (
                            <div className="col-md-4" key={idx}>
                                <div className="card h-100 border-0 rounded-0 shadow-sm aura-feature-card">
                                    <div className="card-body p-4">
                                        <div className="aura-feature-icon mb-3 d-inline-flex align-items-center justify-content-center">
                                            <i className={feature.iconClass} />
                                        </div>
                                        <h3 className="h5 fw-semibold text-aura-primary mb-3">
                                            {feature.title}
                                        </h3>
                                        <ul className="list-unstyled small mb-3">
                                            {feature.features.map((item, i) => (
                                                <li className="mb-1 d-flex align-items-center" key={i}>
                                                    <i className="fa-solid fa-circle-check me-2 text-aura-accent" />
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <button className="btn btn-link p-0 small text-decoration-none text-aura-primary">
                                            Learn more <i className="fa-solid fa-arrow-right ms-1" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* ================= CTA + TABLE ================= */}
            {/* <section className="table-cta-section">
                <div className="container">
                    <div className="row g-4 align-items-center">
                        <div className="col-lg-5">
                            <h2 className="table-cta-main-heading">
                                Streamline Your Clinic Operations{" "}
                                <span className="text-aura-accent">Effortlessly</span>
                            </h2>
                            <p className="description-text-white">
                                For years, clinics have sought a simple, accurate way to manage
                                onboarding, scheduling, and MBS workflows. We&apos;ve finally made it
                                possible.
                            </p>
                            <Button text="Learn More" link="#" />
                        </div>

                        <div className="col-lg-7">
                            <div className="card shadow-sm aura-fee-card">
                                <div className="card-header rounded-0 bg-aura-primary text-white py-2 px-3">
                                    <h3 className="h6 mb-0">Current Fee Runs</h3>
                                </div>
                                <div className="card-body p-3">
                                    <div className="table-responsive">
                                        <table className="table table-sm align-middle mb-0">
                                            <thead>
                                                <tr className="small text-muted">
                                                    <th>Provider</th>
                                                    <th>Date</th>
                                                    <th>Claims</th>
                                                    <th>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody className="small">
                                                {[
                                                    {
                                                        provider: "Dr. Smith",
                                                        date: "Dec 02",
                                                        claims: 12,
                                                        amount: "$2,450",
                                                    },
                                                    {
                                                        provider: "Dr. Johnson",
                                                        date: "Dec 02",
                                                        claims: 8,
                                                        amount: "$1,890",
                                                    },
                                                    {
                                                        provider: "Dr. Williams",
                                                        date: "Dec 01",
                                                        claims: 15,
                                                        amount: "$3,120",
                                                    },
                                                    {
                                                        provider: "Dr. Brown",
                                                        date: "Dec 01",
                                                        claims: 6,
                                                        amount: "$1,245",
                                                    },
                                                ].map((row, i) => (
                                                    <tr key={i}>
                                                        <td className="fw-semibold text-aura-primary">
                                                            {row.provider}
                                                        </td>
                                                        <td className="text-muted">{row.date}</td>
                                                        <td className="text-muted">{row.claims}</td>
                                                        <td className="fw-semibold text-aura-accent">
                                                            {row.amount}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section> */}

            {/* ================= AI SECTION ================= */}
            {/* <section className="aura-ai-section">
                <div className="container text-center mb-4">
                    <p className="low-top-heading">
                        <span className="small fw-semibold">Healthcare Grade AI</span>
                    </p>
                    <h2 className="aura-ai-section-main-heading">
                        AI That Enhances <span>Clinical Outcomes</span> Safely and Responsibly
                    </h2>
                    <p className="mb-0">
                        Aura Clinic uses secure, healthcare-grade AI designed to support clinicians—
                        not replace them. AI improves the experience across onboarding, triage,
                        documentation and compliance.
                    </p>
                </div>

                <div className="container">
                    <div className="row g-4">
                        {aiFeatures.map((feature, idx) => (
                            <div className="col-md-6 col-lg-3" key={idx}>
                                <div className="aura-ai-card h-100">
                                    <div className="aura-ai-icon mb-3">
                                        <i className={feature.iconClass} />
                                    </div>
                                    <h3 className="aura-ai-card-heading">{feature.title}</h3>
                                    <p className="small mb-0 text-white-75">
                                        {feature.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* ================= ONBOARDING STEPS ================= */}
            {/* <section className="onboarding-steps-boxes">
                <div className="container">
                    <div className="text-center mb-4">
                        <h2 className="onboarding-main-box-heading">
                            Simple, Guided Onboarding for{" "}
                            <span className="text-aura-accent">Patients &amp; Clinicians</span>
                        </h2>
                        <p className="description-text-white mb-3">
                            A streamlined 6-step process that makes patient registration effortless.
                        </p>
                    </div>

                    <div className="position-relative mb-4 d-none d-lg-block">
                        <div className="aura-step-line" />
                        <div
                            className="aura-step-line-active"
                            style={{ width: `${progressWidth}%` }}
                        />
                    </div>

                    <div className="row row-cols-2 row-cols-md-3 row-cols-lg-6 g-3">
                        {onboardingSteps.map((step, idx) => {
                            const isActive = idx <= activeStep;
                            return (
                                <div
                                    className="col text-center"
                                    key={idx}
                                    onClick={() => setActiveStep(idx)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div
                                        className={
                                            "aura-step-icon mx-auto mb-2 d-flex align-items-center justify-content-center " +
                                            (isActive ? "aura-step-icon-active" : "bg-white")
                                        }
                                    >
                                        <i className={step.iconClass} />
                                    </div>
                                    <p
                                        className={
                                            "small fw-semibold mb-1 " +
                                            (isActive ? "text-white" : "text-white")
                                        }
                                    >
                                        {step.title}
                                    </p>
                                    <p className="small text-white mb-0">{step.description}</p>
                                </div>
                            );
                        })}
                    </div>

                    <div className="aura-privacy-card mt-4 mx-auto" style={{ maxWidth: 800 }}>
                        <div className="d-flex align-items-start gap-3">
                            <div className="aura-icon-badge-teal flex-shrink-0">
                                <i className="fa-solid fa-lock" />
                            </div>
                            <p className="small text-black mb-0">{privacyDisclaimer}</p>
                        </div>
                    </div>
                </div>
            </section> */}

            {/* ================= FINAL CTA ================= */}
            {/* <section className="final-cta-section">
                <div className="container">
                    <div className="row g-4 align-items-center">
                        <div className="col-lg-6">
                            <h2 className="final-cta-text-heading">
                                Ready to streamline your{" "}
                                <span className="text-aura-accent">healthcare workflow?</span>
                            </h2>
                            <p className="description-text mb-4">
                                See how Aura Clinic transforms patient management, scheduling and
                                billing. Join thousands of Australian clinics already using our platform.
                            </p>

                            <Button text="Explore the Platform" link="#" />
                        </div>

                        <div className="col-lg-6">
                            <div className="aura-screenshot-wrapper p-4">
                                <div className="card shadow border-0">
                                    <div className="card-header bg-light d-flex align-items-center gap-2 rounded-0">
                                        <span className="aura-dot bg-danger" />
                                        <span className="aura-dot bg-warning" />
                                        <span className="aura-dot bg-success" />
                                    </div>
                                    <div className="card-body p-4 rounded-0">
                                        <div className="d-flex align-items-center mb-3 p-3 aura-screenshot-highlight">
                                            <div className="aura-avatar-circle me-3">
                                                <i className="fa-solid fa-user" />
                                            </div>
                                            <div className="flex-grow-1">
                                                <p className="small fw-semibold text-aura-primary mb-1">
                                                    Patient Registration
                                                </p>
                                                <div className="aura-progress-bar rounded-pill" />
                                            </div>
                                            <span className="small fw-bold text-aura-accent">75%</span>
                                        </div>

                                        <div className="row g-3 small">
                                            <div className="col-6">
                                                <div className="bg-light rounded-3 p-3">
                                                    <p className="text-muted mb-1">Personal Info</p>
                                                    <p className="fw-semibold text-aura-primary mb-0">
                                                        ✓ Complete
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="bg-light rounded-3 p-3">
                                                    <p className="text-muted mb-1">Medicare</p>
                                                    <p className="fw-semibold text-aura-primary mb-0">
                                                        ✓ Verified
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section> */}
            <Footer />
        </>
    );
}